import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { IAddress, ICountry } from '../../models/strapi.models';
import {
  deleteStrapi,
  postStrapi,
  putStrapi,
} from '../../services/api.service';
import { logError, logInfo } from '../../services/log.service';
import { flatStrapiObject, getTranslation } from '../../shared/useUtils';
import { IAppOrder } from '../../store/models/store.models';
import { setIsLoading } from '../../store/slices/appSlice';
import { FormLayoutStyle } from '../../styles/components/common/layout';
import { ModalStyle } from '../../styles/components/common/ui';
import { OrderProcessOwnerStyle } from '../../styles/components/order-process';
import AddressBookCreate, {
  IAddressBookCreateFormProps,
} from '../account/adressBook/AdressBookCreateOrModify';
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Toast,
} from '../common/ui';

export interface IOrderProcessOwnerHandle {
  validateStep: () => boolean;
}

interface IOrderProcessOwnerProps {
  countries: ICountry[];
  addresses: IAddress[];
  labels: {
    ownerTitle: string;
    ownerDescription: string;
    addressesEmptyPlaceholder: string;
    ownerCompanyLabel: string;
    ownerCompanyPlaceholder: string;
    ownerTaxIdLabel: string;
    ownerTaxIdPlaceholder: string;
    ownerAddressesTitle: string;
    ownerAddAddressButton: string;
    addressSaveSuccessMessage: string;
    addressSaveErrorMessage: string;
    addressDeleteSuccessMessage: string;
    addressDeleteErrorMessage: string;
    addressDeletePromptMessage: string;
    addressEditButton: string;
    addressDeleteButton: string;
    addressBookCreateOrModify: {
      aliasLabel?: string;
      aliasPlaceholder: string;
      nameLabel?: string;
      namePlaceholder: string;
      addressLabel?: string;
      addressPlaceholder: string;
      postCodeLabel?: string;
      postCodePlaceholder: string;
      cityLabel?: string;
      cityPlaceholder: string;
      countryLabel?: string;
      countryPlaceholder: string;
      referenceSectionTitle: string;
      nameSectionTitle: string;
      addressSectionTitle: string;
      taxIdLabel?: string;
      taxIdPlaceholder?: string;
      title: string;
      saveButton: string;
      deleteButton: string;
    };
    formValidation: {
      requiredField: string;
    };
  };
  order: IAppOrder;
  orderChange: (order: IAppOrder) => void;
  addressesChange: () => void; // Notifies parent that an address has been update or deleted so, get addresses once again
}

interface IComponentState {
  errors: {
    address: string | undefined;
  };
}

const OrderProcessOwner = forwardRef(
  (props: IOrderProcessOwnerProps, ref: any) => {
    const initialComponentState: IComponentState = {
      errors: {
        address: undefined,
      },
    };
    const router = useRouter();
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.auth.user);
    const [state, setState] = useState(initialComponentState);
    const [showToastState, setShowToastState] = useState(false);
    const [showModalState, setShowModalState] = useState(false);
    const [order, setOrder] = useState<IAppOrder>();
    const [canUpsertAddress, setCanUpsertAddress] = useState(false);

    const addressItems = useMemo(() => {
      return props.addresses.map((address) => ({
        id: address.id,
        checked: props.order.addressId === address.id,
        alias: address.alias,
        fullAddress: `${address.name}, ${address.address}, ${
          address.city
        },${' '}
      ${address.postCode},${' '}
      ${
        getTranslation(
          (address.country as ICountry).nameTranslations,
          router.locale || 'en'
        ).text as string
      }`,
      }));
    }, [props.addresses, props.order.addressId, router.locale]);

    const toastProps = useMemo<{
      toastType: 'success' | 'danger';
      toastMessage: string;
    }>(
      () => ({
        toastType: 'success',
        toastMessage: '',
      }),
      []
    );

    const addressOverlayProps = useMemo<{
      currentAddressOverlayProps: IAddressBookCreateFormProps | undefined;
      sourceItem: IAddress | undefined;
    }>(
      () => ({
        currentAddressOverlayProps: undefined,
        sourceItem: undefined,
      }),
      []
    );

    const handleShowToast = useCallback(
      (message: string, type: 'success' | 'danger') => {
        toastProps.toastType = type;
        toastProps.toastMessage = message;
        setShowToastState(true);
      },
      [toastProps]
    );

    const handleDismissToast = useCallback(() => {
      setShowToastState(false);
    }, []);

    const handleShowModal = useCallback(() => {
      setShowModalState(true);
    }, []);

    const handleDismissModal = useCallback(() => {
      setShowModalState(false);
    }, []);

    const validateAddress = useCallback(
      (addressId?: number) => {
        const currentState = { ...state };

        if (!addressId) {
          addressId = order?.addressId;
        }

        if (!addressId) {
          currentState.errors.address =
            props.labels.formValidation.requiredField;
        } else {
          currentState.errors.address = undefined;
        }
        setState(currentState);
      },
      [order?.addressId, props.labels.formValidation.requiredField, state]
    );

    const handleAddressSelected = useCallback(
      (addressId: number) => {
        const currentOrder = { ...props.order };
        currentOrder.addressId = addressId;
        setOrder(currentOrder);
        props.orderChange(currentOrder);
        validateAddress(addressId);
      },
      [props, validateAddress]
    );

    const upsertAddress = useCallback(async () => {
      let address: IAddress | undefined;
      const addressPayload = {
        data: {
          ...addressOverlayProps.currentAddressOverlayProps?.values,
          country: props.countries.find(
            (x) =>
              x.iso ===
              addressOverlayProps.currentAddressOverlayProps?.values.countryCode
          )?.id,
          user: user?.id,
        },
      };

      if ((addressOverlayProps.sourceItem as IAddress)?.id) {
        // Update
        try {
          address = flatStrapiObject(
            (await putStrapi(
              `/addresses/${(addressOverlayProps.sourceItem as IAddress).id}`,
              addressPayload
            )) as any
          );
        } catch (error) {
          logError(['Error', error], 'RegisterPage → registerUser');
        }
      } else {
        //Create
        try {
          address = flatStrapiObject(
            (await postStrapi(`/addresses`, addressPayload)) as any
          );
          logInfo(['Response', address], 'AddressBookPage → handleSaveAddress');
        } catch (error) {
          logError(['Error', error], 'RegisterPage → registerUser');
          throw error;
        }
      }

      logInfo(['Response', address], 'upsertAddress');
      return address;
    }, [
      addressOverlayProps.currentAddressOverlayProps?.values,
      addressOverlayProps.sourceItem,
      props.countries,
      user?.id,
    ]);

    const deleteAddress = useCallback(
      async (addressId: number) => {
        try {
          const addressResponse = (await deleteStrapi(
            `/addresses/${addressId}`
          )) as any;
          logInfo(
            ['Response', addressResponse],
            'AddressBookPage → handleSaveAddress'
          );
        } catch (error) {
          logError(['Error', error], 'AddressBookPage → handleDeleteAddress');
          throw error;
        }

        handleAddressSelected(addressId);
      },
      [handleAddressSelected]
    );

    const handleSetCurrentAddress = useCallback(
      (sourceItem?: IAddress) => {
        const addressProps: IAddressBookCreateFormProps = {
          labels: {
            aliasLabel: props.labels.addressBookCreateOrModify.aliasLabel,
            aliasPlaceholder:
              props.labels.addressBookCreateOrModify.aliasPlaceholder,
            nameLabel: props.labels.addressBookCreateOrModify.nameLabel,
            namePlaceholder:
              props.labels.addressBookCreateOrModify.namePlaceholder,
            addressLabel: props.labels.addressBookCreateOrModify.addressLabel,
            addressPlaceholder:
              props.labels.addressBookCreateOrModify.addressPlaceholder,
            postCodeLabel: props.labels.addressBookCreateOrModify.postCodeLabel,
            postCodePlaceholder:
              props.labels.addressBookCreateOrModify.postCodePlaceholder,
            cityLabel: props.labels.addressBookCreateOrModify.cityLabel,
            cityPlaceholder:
              props.labels.addressBookCreateOrModify.cityPlaceholder,
            countryLabel: props.labels.addressBookCreateOrModify.countryLabel,
            countryPlaceholder:
              props.labels.addressBookCreateOrModify.countryPlaceholder,
            referenceSectionTitle:
              props.labels.addressBookCreateOrModify.referenceSectionTitle,
            nameSectionTitle:
              props.labels.addressBookCreateOrModify.nameSectionTitle,
            addressSectionTitle:
              props.labels.addressBookCreateOrModify.addressSectionTitle,
            taxIdLabel: props.labels.addressBookCreateOrModify.taxIdLabel,
            taxIdPlaceholder:
              props.labels.addressBookCreateOrModify.taxIdPlaceholder,
            formValidation: {
              requiredField: props.labels.formValidation.requiredField,
            },
          },
          onFormChange: (data: IAddressBookCreateFormProps) => {
            addressOverlayProps.currentAddressOverlayProps = {
              ...data,
            };

            // Validate data
            const isDataValid =
              !!addressOverlayProps.currentAddressOverlayProps &&
              !!addressOverlayProps.currentAddressOverlayProps.values &&
              !!addressOverlayProps.currentAddressOverlayProps.values.address &&
              !!addressOverlayProps.currentAddressOverlayProps.values.city &&
              !!addressOverlayProps.currentAddressOverlayProps.values
                .countryCode &&
              !!addressOverlayProps.currentAddressOverlayProps.values.name &&
              !!addressOverlayProps.currentAddressOverlayProps.values
                .postCode &&
              !!addressOverlayProps.currentAddressOverlayProps.values;

            setCanUpsertAddress(isDataValid);

            const currentOrder = { ...props.order } as IAppOrder;
            setOrder(currentOrder);
            props.orderChange({ ...currentOrder });
          },
          values: {
            name: sourceItem?.name || '',
            alias: sourceItem?.alias || '',
            address: sourceItem?.address || '',
            postCode: sourceItem?.postCode || '',
            city: sourceItem?.city || '',
            countryCode: (sourceItem?.country as ICountry)?.iso || '',
            taxId: sourceItem?.taxId || '',
          },
          countries: props.countries
            .map((x) => ({
              label: getTranslation(x.nameTranslations, router.locale || 'en')
                .text as string,
              value: x.iso,
            }))
            .sort((a, b) => {
              if (a.label < b.label) {
                return -1;
              }
              if (a.label > b.label) {
                return 1;
              }
              return 0;
            }),
        };

        addressOverlayProps.currentAddressOverlayProps = {
          ...addressProps,
        };

        addressOverlayProps.sourceItem = sourceItem;
      },
      [addressOverlayProps, props, router.locale]
    );

    const handleUpsertAddress = useCallback(async () => {
      dispatch(setIsLoading(true));
      handleDismissModal();

      try {
        const result = await upsertAddress();
        props.addressesChange();
        handleShowToast(props.labels.addressSaveSuccessMessage, 'success');

        if (!!result) {
          handleAddressSelected(result.id as number);
        }
      } catch (error) {
        handleShowToast(props.labels.addressSaveErrorMessage, 'danger');
      }

      dispatch(setIsLoading(false));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dispatch,
      props.labels.addressSaveSuccessMessage,
      props.labels.addressSaveErrorMessage,
      props.addresses,
    ]);

    const handleDeleteAddress = useCallback(
      async (addressId: number) => {
        // TODO: replace by a custom confirmation prompt
        const confirm = window.confirm(props.labels.addressDeletePromptMessage);
        if (confirm && addressId) {
          dispatch(setIsLoading(true));
          const currentOrder = { ...props.order } as IAppOrder;

          try {
            await deleteAddress(addressId);

            if (currentOrder.addressId === addressId) {
              currentOrder.addressId = undefined;
              props.orderChange(currentOrder);
              setOrder(currentOrder);
            }

            props.addressesChange();
            handleDismissModal();
            handleShowToast(
              props.labels.addressDeleteSuccessMessage,
              'success'
            );
          } catch (error) {
            handleShowToast(props.labels.addressDeleteErrorMessage, 'danger');
          }

          dispatch(setIsLoading(false));
        }
      },
      [deleteAddress, dispatch, handleDismissModal, handleShowToast, props]
    );

    useImperativeHandle(ref, () => ({
      validateStep() {
        validateAddress();
        return !!order?.addressId;
      },
    }));

    useEffect(() => {
      setOrder({ ...props.order });
    }, [props.order]);

    return (
      <>
        <div className={OrderProcessOwnerStyle['order-process-owner']}>
          <h3>{props.labels.ownerTitle}</h3>
          <div className="left-padded">
            <div>
              <ReactMarkdown>{props.labels.ownerDescription}</ReactMarkdown>
            </div>
          </div>

          <h4>{props.labels.ownerAddressesTitle}</h4>

          <div className="left-padded">
            {addressItems.length > 0 &&
              addressItems.map((address, index) => (
                <div
                  key={`address-${address.id}-${index}`}
                  className={`${
                    OrderProcessOwnerStyle['order-process-owner-address']
                  } ${
                    state.errors.address && OrderProcessOwnerStyle['invalid']
                  }`}>
                  <input
                    type="radio"
                    name="owner-addresses"
                    id={`label-${index}`}
                    checked={address.checked}
                    onChange={(ev: any) => {
                      if (ev.target.checked) {
                        handleAddressSelected(address.id as number);
                      }
                    }}
                  />
                  <label htmlFor={`label-${index}`}>
                    <div
                      className={
                        OrderProcessOwnerStyle[
                          'order-process-owner-address-label'
                        ]
                      }>
                      <p>
                        <strong>{address.alias}</strong>
                      </p>
                      <p>{address.fullAddress}</p>
                    </div>
                  </label>
                  <div
                    className={
                      OrderProcessOwnerStyle[
                        'order-process-owner-address-actions'
                      ]
                    }>
                    <Button
                      onClick={() => {
                        const addressToEdit = props.addresses.find(
                          (x) => x.id === address.id
                        );
                        handleSetCurrentAddress(addressToEdit);
                        handleShowModal();
                      }}
                      color="minimal-small">
                      {props.labels.addressEditButton}
                    </Button>
                    <Button
                      onClick={() => handleDeleteAddress(address.id as number)}
                      color="minimal-small">
                      {props.labels.addressDeleteButton}
                    </Button>
                  </div>
                </div>
              ))}

            {addressItems.length === 0 && (
              <div
                className={
                  OrderProcessOwnerStyle['order-process-owner-address-empty']
                }>
                {props.labels.addressesEmptyPlaceholder}
              </div>
            )}

            {state.errors.address && (
              <div className={FormLayoutStyle['formError']}>
                <span>{state.errors.address}</span>
              </div>
            )}

            <hr className="separator-thin" />

            <Button
              className="is-text-align-center"
              color="cta-subtle"
              onClick={() => {
                handleSetCurrentAddress();
                handleShowModal();
              }}>
              {props.labels.ownerAddAddressButton}
            </Button>
          </div>
        </div>

        {/* Editor modal */}
        <Modal show={showModalState}>
          <ModalHeader>
            <h3>{props.labels.addressBookCreateOrModify.title}</h3>
            <Image
              quality={90}
              src={'/assets/images/icons/CloseIcon.svg'}
              className={ModalStyle['close-icon']}
              alt="Close"
              width={32}
              height={32}
              onClick={handleDismissModal}
            />
          </ModalHeader>
          <ModalContent>
            {addressOverlayProps.currentAddressOverlayProps && (
              <AddressBookCreate
                {...addressOverlayProps.currentAddressOverlayProps}
              />
            )}
          </ModalContent>
          <ModalFooter className="buttons-toolbar">
            <Button onClick={handleUpsertAddress} disabled={!canUpsertAddress}>
              {props.labels.addressBookCreateOrModify.saveButton}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Toast */}
        <Toast
          type={toastProps.toastType}
          show={showToastState}
          onDismiss={handleDismissToast}>
          {toastProps.toastMessage}
        </Toast>
      </>
    );
  }
);

OrderProcessOwner.displayName = 'OrderProcessOwner';

export default OrderProcessOwner;
